import React, { useState, useId, useMemo } from "react";
import { cn } from "@/lib/utils";

export interface TrendPoint {
  period: string; // e.g. "2026-01", "Jan", etc.
  value: number;
  label?: string; // Optional friendly label
}

interface AreaTrendChartProps {
  data: TrendPoint[];
  height?: number;
  formatValue?: (v: number) => string;
  color?: string; // Hex color for the stroke
  gradientFrom?: string;
  gradientTo?: string;
  className?: string;
  yAxisLabel?: string;
}

export function AreaTrendChart({
  data,
  height = 200,
  formatValue = (v) => `₹${v.toLocaleString("en-IN")}`,
  color = "#2563eb",
  gradientFrom = "rgba(37, 99, 235, 0.35)",
  gradientTo = "rgba(37, 99, 235, 0.0)",
  className,
  yAxisLabel,
}: AreaTrendChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const gradientId = useId();

  const padding = { top: 20, right: 20, bottom: 32, left: 24 };

  const { points, maxVal, minVal, pathD, areaD } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: [], maxVal: 0, minVal: 0, pathD: "", areaD: "" };
    }

    const values = data.map((d) => Number(d.value) || 0);
    const rawMax = Math.max(0, ...values);
    // Add 10% headroom
    const maxVal = rawMax === 0 ? 100 : rawMax * 1.12;
    const minVal = 0;

    const width = 600; // Virtual width for SVG viewBox
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const pts = data.map((d, i) => {
      const x =
        data.length === 1
          ? padding.left + plotWidth / 2
          : padding.left + (i / (data.length - 1)) * plotWidth;
      const normalizedY = (Number(d.value) || 0) / maxVal;
      const y = padding.top + plotHeight - normalizedY * plotHeight;
      return { x, y, ...d };
    });

    if (pts.length === 0) return { points: [], maxVal, minVal, pathD: "", areaD: "" };

    // Build smooth cubic bezier curve through points
    let pathD = `M ${pts[0]!.x} ${pts[0]!.y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      // Indexes are within 0..length-1 by the loop bounds
      const p1 = pts[i]!;
      const p2 = pts[i + 1]!;
      const p0 = i > 0 ? pts[i - 1]! : p1;
      const p3 = i < pts.length - 2 ? pts[i + 2]! : p2;

      // Tension factor
      const tension = 0.2;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      pathD += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }

    // Build closed area path for gradient
    const lastX = pts[pts.length - 1]!.x;
    const firstX = pts[0]!.x;
    const baseY = height - padding.bottom;
    const areaD = `${pathD} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`;

    return { points: pts, maxVal, minVal, pathD, areaD };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-48 text-xs text-muted-foreground", className)}>
        No trend data available
      </div>
    );
  }

  const activePoint = hoveredIdx !== null ? points[hoveredIdx] : null;

  return (
    <div className={cn("relative w-full select-none", className)}>
      {/* SVG Responsive Container */}
      <svg
        viewBox={`0 0 600 ${height}`}
        className="w-full h-auto overflow-visible"
        style={{ minHeight: height }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        <defs>
          <linearGradient id={`grad-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="90%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
          <filter id={`dot-glow-${gradientId}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor={color} floodOpacity="0.4" />
          </filter>
        </defs>

        {/* Horizontal guide lines */}
        {[0, 0.33, 0.66, 1].map((pct, idx) => {
          const y = padding.top + (height - padding.top - padding.bottom) * pct;
          return (
            <line
              key={idx}
              x1={padding.left}
              y1={y}
              x2={600 - padding.right}
              y2={y}
              stroke="currentColor"
              className="text-border/60"
              strokeDasharray={pct === 1 ? undefined : "3 3"}
              strokeWidth="1"
            />
          );
        })}

        {/* Gradient Area Fill */}
        <path d={areaD} fill={`url(#grad-${gradientId})`} />

        {/* Stroke Curve Line */}
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points & hover triggers */}
        {points.map((pt, i) => {
          const isHovered = hoveredIdx === i;
          return (
            <g key={pt.period + i}>
              {/* Invisible wide hover target column */}
              <rect
                x={pt.x - 18}
                y={padding.top}
                width={36}
                height={height - padding.top - padding.bottom}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIdx(i)}
              />

              {/* Data circle dot */}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={isHovered ? 6 : 3.5}
                fill="#ffffff"
                stroke={color}
                strokeWidth={isHovered ? 3 : 2}
                className="transition-all duration-150 pointer-events-none"
                filter={isHovered ? `url(#dot-glow-${gradientId})` : undefined}
              />

              {/* X-axis tick label */}
              <text
                x={pt.x}
                y={height - 10}
                textAnchor="middle"
                className={cn(
                  "text-[10px] font-semibold transition-colors pointer-events-none",
                  isHovered ? "fill-primary font-bold" : "fill-muted-foreground"
                )}
              >
                {pt.label || pt.period}
              </text>
            </g>
          );
        })}

        {/* Vertical crosshair on hover */}
        {activePoint && (
          <line
            x1={activePoint.x}
            y1={padding.top}
            x2={activePoint.x}
            y2={height - padding.bottom}
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray="2 2"
            className="pointer-events-none opacity-70"
          />
        )}
      </svg>

      {/* Floating Tooltip */}
      {activePoint && (
        <div
          className="pointer-events-none absolute z-30 -translate-x-1/2 -translate-y-full bg-card/95 backdrop-blur-md border border-border/80 shadow-xl rounded-lg px-3 py-1.5 text-xs animate-in fade-in-0 duration-150"
          style={{
            left: `${(activePoint.x / 600) * 100}%`,
            top: `${(activePoint.y / height) * 100}%`,
            marginTop: -10,
          }}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {activePoint.label || activePoint.period}
          </div>
          <div className="font-extrabold text-foreground text-sm tabular-nums mt-0.5">
            {formatValue(activePoint.value)}
          </div>
        </div>
      )}
    </div>
  );
}
